import { type FormEvent, useEffect, useState } from "react";

import type {
  BiologicalSex,
  Child,
  ChildActivityLevel,
  ChildCreate,
  ChildTarget,
  ChildUpdate,
} from "@sytofood/shared";

import { useModalScrollLock } from "../hooks/use-modal-scroll-lock";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { useLang } from "../lib/i18n";
import { type ChildrenCopy, childrenCopy } from "../lib/i18n/children";
import { useProfile } from "../lib/profile-context";
import { ScreenShell } from "./screen-shell";
import {
  Eyebrow,
  FormField,
  H1,
  Input,
  MonoMeta,
  PrimaryButton,
  SecondaryButton,
  TopBar,
} from "./ui/primitives";

type Props = {
  householdId: string;
  onBack: () => void;
};

const ACTIVITY_KEYS: ChildActivityLevel[] = [
  "sedentary",
  "light",
  "moderate",
  "high",
  "very-high",
];

// Computes integer years from a YYYY-MM-DD DOB. Mirrors the server-side
// ageFromDob helper conceptually (clamped to >=0 so a typo in the future
// doesn't produce a negative number on screen).
function ageYearsFromDob(dob: string): number {
  const parts = dob.split("-").map((s) => Number(s));
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (
    y === undefined ||
    m === undefined ||
    d === undefined ||
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(d)
  ) {
    return 0;
  }
  const now = new Date();
  let age = now.getFullYear() - y;
  const monthDiff = now.getMonth() + 1 - m;
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d)) age -= 1;
  return Math.max(0, age);
}

// Form state mirrors ChildCreateSchema except numeric fields stay as strings
// while the user is typing (empty string = unset).
type FormState = {
  name: string;
  dateOfBirth: string;
  biologicalSex: BiologicalSex | "";
  heightCm: string;
  weightKg: string;
  activityLevel: ChildActivityLevel | "";
  kcalTargetOverride: string;
};

const EMPTY_FORM: FormState = {
  name: "",
  dateOfBirth: "",
  biologicalSex: "",
  heightCm: "",
  weightKg: "",
  activityLevel: "",
  kcalTargetOverride: "",
};

function formFromChild(child: Child): FormState {
  return {
    name: child.name,
    dateOfBirth: child.dateOfBirth,
    biologicalSex: child.biologicalSex,
    heightCm: child.heightCm !== null ? String(child.heightCm) : "",
    weightKg: child.weightKg !== null ? String(child.weightKg) : "",
    activityLevel: child.activityLevel ?? "",
    kcalTargetOverride:
      child.kcalTargetOverride !== null ? String(child.kcalTargetOverride) : "",
  };
}

function numOrUndefined(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  const n = Number(trimmed.replace(",", "."));
  return Number.isFinite(n) ? n : undefined;
}

function numOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

// Subset of ChildrenCopy keys whose value is a plain `string` (not a fn /
// record). Used to mark form-validation error codes so `t[code]` is a
// `string` at the type level without an unsafe cast.
type ChildErrorKey = "chErrName" | "chErrDob" | "chErrSex" | "chErrGeneric";

function buildCreatePayload(form: FormState): ChildCreate | { error: ChildErrorKey } {
  const name = form.name.trim();
  if (name.length === 0) return { error: "chErrName" };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.dateOfBirth)) return { error: "chErrDob" };
  if (form.biologicalSex === "") return { error: "chErrSex" };
  const payload: ChildCreate = {
    name,
    dateOfBirth: form.dateOfBirth,
    biologicalSex: form.biologicalSex,
  };
  const h = numOrUndefined(form.heightCm);
  if (h !== undefined) payload.heightCm = h;
  const w = numOrUndefined(form.weightKg);
  if (w !== undefined) payload.weightKg = w;
  if (form.activityLevel !== "") payload.activityLevel = form.activityLevel;
  const o = numOrUndefined(form.kcalTargetOverride);
  if (o !== undefined) payload.kcalTargetOverride = Math.round(o);
  return payload;
}

function buildUpdatePayload(form: FormState): ChildUpdate | { error: ChildErrorKey } {
  const name = form.name.trim();
  if (name.length === 0) return { error: "chErrName" };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.dateOfBirth)) return { error: "chErrDob" };
  if (form.biologicalSex === "") return { error: "chErrSex" };
  const o = numOrNull(form.kcalTargetOverride);
  // For update, send heightCm / weightKg / activity / override as explicit
  // nullable values so clearing a field actually clears it on the server.
  return {
    name,
    dateOfBirth: form.dateOfBirth,
    biologicalSex: form.biologicalSex,
    heightCm: numOrNull(form.heightCm),
    weightKg: numOrNull(form.weightKg),
    activityLevel: form.activityLevel === "" ? null : form.activityLevel,
    kcalTargetOverride: o === null ? null : Math.round(o),
  };
}

export function ChildrenScreen({ householdId, onBack }: Props) {
  const { lang } = useLang();
  const t = childrenCopy[lang];
  const { state } = useAuth();
  const { children, refreshChildren, loading } = useProfile();
  void state; // auth presence already implied by parent route guard
  void householdId;

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<FormState>(EMPTY_FORM);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Per-row edit state. Only one row is in edit mode at a time, mirroring
  // the meal-confirmation-screen editingIdx pattern.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; msg: string } | null>(null);

  const [pendingDelete, setPendingDelete] = useState<Child | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Lazy-loaded per-child Henry estimates. Keyed by child id; null = not
  // requested yet, undefined slot = request returned no target (incomplete).
  const [targets, setTargets] = useState<Record<string, ChildTarget>>({});

  // Fetch the target hint for each child once per render-batch. Calls are
  // cheap server-side (no AI) so we don't bother debouncing.
  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      const fresh: Record<string, ChildTarget> = {};
      await Promise.all(
        children.map(async (c) => {
          try {
            const target = await api.getChildTarget(householdId, c.id);
            if (!cancelled) fresh[c.id] = target;
          } catch {
            // Non-fatal: row just won't show the kcal hint
          }
        }),
      );
      if (!cancelled) setTargets(fresh);
    }
    if (children.length > 0) {
      void loadAll();
    } else {
      setTargets({});
    }
    return () => {
      cancelled = true;
    };
  }, [children, householdId]);

  async function handleAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setAddError(null);
    const built = buildCreatePayload(addForm);
    if ("error" in built) {
      setAddError(t[built.error]);
      return;
    }
    setAdding(true);
    try {
      await api.createChild(householdId, built);
      await refreshChildren();
      setAddForm(EMPTY_FORM);
      setAddOpen(false);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : t.chErrGeneric);
    } finally {
      setAdding(false);
    }
  }

  function startEdit(child: Child) {
    setEditingId(child.id);
    setEditForm(formFromChild(child));
    setRowError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
    setRowError(null);
  }

  async function handleEditSave(child: Child, e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setRowError(null);
    const built = buildUpdatePayload(editForm);
    if ("error" in built) {
      setRowError({ id: child.id, msg: t[built.error] });
      return;
    }
    setSavingId(child.id);
    try {
      await api.updateChild(householdId, child.id, built);
      await refreshChildren();
      setEditingId(null);
    } catch (err) {
      setRowError({
        id: child.id,
        msg: err instanceof Error ? err.message : t.chErrGeneric,
      });
    } finally {
      setSavingId(null);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeletingId(pendingDelete.id);
    try {
      await api.deleteChild(householdId, pendingDelete.id);
      await refreshChildren();
      setPendingDelete(null);
      if (editingId === pendingDelete.id) cancelEdit();
    } catch {
      // swallow — pendingDelete stays open, user can retry
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <ScreenShell hideHeader>
      <div style={{ marginBottom: 18 }}>
        <TopBar back={t.chScreenBack} title={t.chScreenTitle} onBack={onBack} />
      </div>

      <Eyebrow>{t.chSectionEyebrow}</Eyebrow>
      <div style={{ marginTop: 8 }}>
        <H1 accent=".">{t.chScreenHeading}</H1>
      </div>
      <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>{t.chScreenIntro}</p>

      {/* Add child CTA / inline form */}
      <section style={{ marginTop: 22 }}>
        {!addOpen ? (
          <button
            type="button"
            onClick={() => {
              setAddOpen(true);
              setAddForm(EMPTY_FORM);
              setAddError(null);
            }}
            className="uppercase"
            style={{
              width: "100%",
              padding: "14px 18px",
              borderRadius: 12,
              background: "var(--accent)",
              color: "var(--accent-ink)",
              border: "none",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              cursor: "pointer",
              boxShadow: "var(--shadow-cta)",
            }}
          >
            + {t.chAddChild}
          </button>
        ) : (
          <form
            onSubmit={handleAdd}
            style={{
              padding: 16,
              borderRadius: 14,
              background: "var(--surface)",
              boxShadow: "0 0 0 1px var(--nav-border)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <ChildFormFields form={addForm} onChange={setAddForm} t={t} />
            {addError ? (
              <p
                className="text-[12px] px-3 py-2 rounded-md"
                style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
              >
                {addError}
              </p>
            ) : null}
            <div className="flex flex-col gap-2" style={{ marginTop: 4 }}>
              <PrimaryButton type="submit" disabled={adding}>
                {adding ? t.chSaving : t.chSave}
              </PrimaryButton>
              <SecondaryButton
                type="button"
                onClick={() => {
                  setAddOpen(false);
                  setAddForm(EMPTY_FORM);
                  setAddError(null);
                }}
                disabled={adding}
              >
                {t.chCancel}
              </SecondaryButton>
            </div>
          </form>
        )}
      </section>

      {/* Existing children list */}
      <section style={{ marginTop: 26 }}>
        {loading && children.length === 0 ? (
          <p className="text-[13px] py-4" style={{ color: "var(--muted)" }}>
            …
          </p>
        ) : children.length === 0 ? (
          <div
            style={{
              padding: 18,
              borderRadius: 12,
              background: "var(--surface-2)",
              color: "var(--ink-2)",
            }}
          >
            <p style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
              {t.chListEmpty}
            </p>
            <p style={{ fontSize: 13, marginTop: 4, color: "var(--ink-2)" }}>
              {t.chListEmptyHint}
            </p>
          </div>
        ) : (
          children.map((child) => {
            const isEditing = editingId === child.id;
            const target = targets[child.id];
            const ageYears = ageYearsFromDob(child.dateOfBirth);
            const isSaving = savingId === child.id;
            const isDeleting = deletingId === child.id;
            return (
              <div
                key={child.id}
                style={{
                  padding: "14px 0",
                  borderTop: "1px solid var(--nav-border)",
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => (isEditing ? cancelEdit() : startEdit(child))}
                    className="flex-1 text-left min-w-0"
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>
                      {child.name}
                    </div>
                    <MonoMeta>
                      {t.chRowAgeYears(ageYears).toUpperCase()}
                      {target ? " · " : ""}
                      {target
                        ? target.source === "incomplete"
                          ? (target.kcalTarget === null && ageYears < 3
                              ? t.chTargetIncompleteAge
                              : t.chTargetIncomplete
                            ).toUpperCase()
                          : target.kcalTarget !== null
                            ? t.chRowKcalApprox(target.kcalTarget).toUpperCase()
                            : t.chTargetIncomplete.toUpperCase()
                        : ""}
                    </MonoMeta>
                  </button>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => (isEditing ? cancelEdit() : startEdit(child))}
                      style={{
                        padding: "5px 10px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: isEditing ? "var(--accent)" : "var(--ink-2)",
                        boxShadow: "inset 0 0 0 1px var(--nav-border)",
                        borderRadius: 6,
                        background: "transparent",
                        border: "none",
                      }}
                    >
                      {isEditing ? t.chCollapse : t.chEdit}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDelete(child)}
                      style={{
                        padding: "5px 10px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--bad-fg)",
                        background: "transparent",
                        border: "none",
                      }}
                    >
                      {t.chDelete}
                    </button>
                  </div>
                </div>
                {isEditing ? (
                  <form
                    onSubmit={(e) => handleEditSave(child, e)}
                    style={{
                      marginTop: 12,
                      padding: 14,
                      borderRadius: 12,
                      background: "var(--surface)",
                      boxShadow: "0 0 0 1px var(--nav-border)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    <ChildFormFields form={editForm} onChange={setEditForm} t={t} />
                    {rowError && rowError.id === child.id ? (
                      <p
                        className="text-[12px] px-3 py-2 rounded-md"
                        style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
                      >
                        {rowError.msg}
                      </p>
                    ) : null}
                    <div className="flex flex-col gap-2">
                      <PrimaryButton type="submit" disabled={isSaving || isDeleting}>
                        {isSaving ? t.chSaving : t.chSave}
                      </PrimaryButton>
                      <SecondaryButton type="button" onClick={cancelEdit} disabled={isSaving}>
                        {t.chCancel}
                      </SecondaryButton>
                    </div>
                  </form>
                ) : null}
              </div>
            );
          })
        )}
      </section>

      {pendingDelete ? (
        <DeleteChildModal
          name={pendingDelete.name}
          submitting={deletingId === pendingDelete.id}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
        />
      ) : null}
    </ScreenShell>
  );
}

function ChildFormFields({
  form,
  onChange,
  t,
}: {
  form: FormState;
  onChange: (next: FormState) => void;
  t: ChildrenCopy;
}) {
  return (
    <>
      <FormField label={t.chFormName}>
        <Input
          type="text"
          required
          maxLength={80}
          value={form.name}
          onChange={(e) => onChange({ ...form, name: e.target.value })}
          placeholder={t.chFormNamePlaceholder}
        />
      </FormField>
      <div className="grid grid-cols-2 gap-2">
        <FormField label={t.chFormDob}>
          <Input
            type="date"
            required
            value={form.dateOfBirth}
            onChange={(e) => onChange({ ...form, dateOfBirth: e.target.value })}
          />
        </FormField>
        <FormField label={t.chFormSex}>
          <div className="flex gap-2">
            {(
              [
                ["female", t.chFormSexFemale],
                ["male", t.chFormSexMale],
              ] as const
            ).map(([v, label]) => {
              const active = form.biologicalSex === v;
              return (
                <button
                  key={v}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onChange({ ...form, biologicalSex: v })}
                  className="flex-1 text-[12px] font-bold"
                  style={{
                    padding: "10px 0",
                    borderRadius: 10,
                    fontFamily: "var(--font-mono)",
                    background: active ? "var(--ink)" : "var(--surface)",
                    color: active ? "var(--bg)" : "var(--ink-2)",
                    boxShadow: active ? "none" : "0 0 0 1px var(--nav-border)",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <FormField label={t.chFormHeight}>
          <Input
            type="number"
            inputMode="decimal"
            min={40}
            max={220}
            step={0.5}
            value={form.heightCm}
            onChange={(e) => onChange({ ...form, heightCm: e.target.value })}
            placeholder={t.chFormHeightPlaceholder}
          />
        </FormField>
        <FormField label={t.chFormWeight}>
          <Input
            type="number"
            inputMode="decimal"
            min={5}
            max={150}
            step={0.1}
            value={form.weightKg}
            onChange={(e) => onChange({ ...form, weightKg: e.target.value })}
            placeholder={t.chFormWeightPlaceholder}
          />
        </FormField>
      </div>
      <FormField label={t.chFormActivity}>
        <div className="flex flex-wrap gap-1.5">
          {ACTIVITY_KEYS.map((k) => {
            const active = form.activityLevel === k;
            return (
              <button
                key={k}
                type="button"
                aria-pressed={active}
                onClick={() =>
                  onChange({ ...form, activityLevel: active ? "" : k })
                }
                className="text-[11px] font-bold"
                style={{
                  padding: "8px 12px",
                  borderRadius: 10,
                  fontFamily: "var(--font-mono)",
                  background: active ? "var(--ink)" : "var(--surface)",
                  color: active ? "var(--bg)" : "var(--ink-2)",
                  boxShadow: active ? "none" : "0 0 0 1px var(--nav-border)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {t.chActivityOptions[k]}
              </button>
            );
          })}
        </div>
      </FormField>
      <FormField label={t.chFormOverride} hint={t.chFormOverrideHint}>
        <Input
          type="number"
          inputMode="numeric"
          min={500}
          max={5000}
          step={50}
          value={form.kcalTargetOverride}
          onChange={(e) => onChange({ ...form, kcalTargetOverride: e.target.value })}
          placeholder={t.chFormOverridePlaceholder}
        />
      </FormField>
    </>
  );
}

function DeleteChildModal({
  name,
  submitting,
  onCancel,
  onConfirm,
}: {
  name: string;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { lang } = useLang();
  const t = childrenCopy[lang];
  // Component only mounts when open, but pass `true` explicitly so the hook
  // contract is obvious. <dialog open> (no showModal) does not put the
  // dialog in the top layer, so iOS WKWebView will scroll the body
  // underneath without this lock (gotchas handoff §4.1).
  useModalScrollLock(true);
  return (
    <dialog
      open
      aria-modal="true"
      className="fixed inset-0 z-50 m-0 flex h-full w-full items-center justify-center bg-transparent p-4"
    >
      <button
        type="button"
        aria-label={t.chDeleteCancel}
        onClick={onCancel}
        className="absolute inset-0"
        style={{
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      />
      <div
        className="relative w-full max-w-sm p-5 flex flex-col gap-4"
        style={{
          background: "var(--surface)",
          borderRadius: 18,
          boxShadow: "0 0 0 1px var(--nav-border)",
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: "-0.03em",
            color: "var(--ink)",
          }}
        >
          {t.chDeleteConfirm(name)}
        </h2>
        <div className="flex flex-col gap-2">
          <PrimaryButton onClick={onConfirm} disabled={submitting}>
            {submitting ? t.chDeleting : t.chDeleteConfirmCta}
          </PrimaryButton>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="h-11 px-4 text-[13px] font-medium"
            style={{ color: "var(--ink-2)", background: "transparent", border: "none" }}
          >
            {t.chDeleteCancel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
